<template>
  <el-card>
    <el-form :inline="true" @submit.prevent>
      <el-form-item>
        <el-input v-model.trim="keyword" placeholder="手机号/昵称" @keyup.enter="onSearchUsers" />
      </el-form-item>
      <el-form-item>
        <el-select v-model="userRoleId" clearable placeholder="角色筛选" style="width: 180px;">
          <el-option v-for="r in roleOptions" :key="r.role_id" :label="r.name" :value="r.role_id" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-date-picker
          v-model="userDateRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearchUsers">搜索用户</el-button>
        <el-button @click="onResetUsers">重置</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="userList" stripe border v-loading="loadingUsers">
      <el-table-column prop="user_id" label="用户ID" width="90" />
      <el-table-column prop="user_phone" label="手机号" min-width="130" />
      <el-table-column prop="user_nickname" label="昵称" min-width="120" />
      <el-table-column prop="session_count" label="会话数" width="100" />
      <el-table-column prop="message_count" label="消息数" width="100" />
      <el-table-column label="最后聊天时间" min-width="180">
        <template #default="{ row }">{{ fmt(row.last_chat_at) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openUserMessages(row)">查看记录</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div style="margin-top: 12px; display: flex; justify-content: flex-end;">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="usersTotal"
        :current-page="usersQuery.page"
        :page-size="usersQuery.page_size"
        @current-change="onUsersPageChange"
      />
    </div>
  </el-card>

  <el-drawer v-model="drawerVisible" size="980px" :title="drawerTitle">
    <el-form :inline="true" @submit.prevent>
      <el-form-item>
        <el-input
          v-model.trim="messageKeyword"
          placeholder="搜索消息内容/角色名/会话ID"
          @keyup.enter="onSearchMessages"
          style="width: 320px;"
        />
      </el-form-item>
      <el-form-item>
        <el-select v-model="messageRoleId" clearable placeholder="角色筛选" style="width: 180px;">
          <el-option v-for="r in roleOptions" :key="r.role_id" :label="r.name" :value="r.role_id" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-date-picker
          v-model="messageDateRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="onSearchMessages">搜索记录</el-button>
        <el-button @click="onResetMessages">重置</el-button>
        <el-button @click="onExportMessages">导出CSV</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="messageList" stripe border v-loading="loadingMessages">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="session_id" label="会话ID" min-width="170" show-overflow-tooltip />
      <el-table-column prop="role_name" label="角色" min-width="120" />
      <el-table-column label="发送方" width="100">
        <template #default="{ row }">
          <el-tag :type="row.sender === 'user' ? 'primary' : 'success'">{{ row.sender === 'user' ? '用户' : 'AI' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="消息内容" min-width="320">
        <template #default="{ row }">
          <div v-html="highlightText(row.content, messageKeyword)"></div>
        </template>
      </el-table-column>
      <el-table-column label="发送时间" min-width="180">
        <template #default="{ row }">{{ fmt(row.created_at) }}</template>
      </el-table-column>
    </el-table>

    <div style="margin-top: 12px; display: flex; justify-content: flex-end;">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="messagesTotal"
        :current-page="messagesQuery.page"
        :page-size="messagesQuery.page_size"
        @current-change="onMessagesPageChange"
      />
    </div>
  </el-drawer>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { adminApi } from '../api/admin'

const keyword = ref('')
const userList = ref([])
const usersTotal = ref(0)
const loadingUsers = ref(false)
const usersQuery = ref({
  page: 1,
  page_size: 20
})
const userRoleId = ref()
const userDateRange = ref([])
const roleOptions = ref([])

const drawerVisible = ref(false)
const loadingMessages = ref(false)
const currentUser = ref(null)
const messageKeyword = ref('')
const messageRoleId = ref()
const messageDateRange = ref([])
const messageList = ref([])
const messagesTotal = ref(0)
const messagesQuery = ref({
  page: 1,
  page_size: 20
})

const drawerTitle = computed(() => {
  if (!currentUser.value) return '聊天记录'
  return `聊天记录 - ${currentUser.value.user_phone || '-'} (${currentUser.value.user_nickname || '-'})`
})

const fmt = (v) => (v ? new Date(v).toLocaleString() : '-')

async function loadUsers() {
  loadingUsers.value = true
  try {
    const startAt = userDateRange.value?.[0] || ''
    const endAt = userDateRange.value?.[1] || ''
    const data = await adminApi.sessionUsers({
      keyword: keyword.value,
      role_id: userRoleId.value || '',
      start_at: startAt,
      end_at: endAt,
      page: usersQuery.value.page,
      page_size: usersQuery.value.page_size
    })
    userList.value = data.list || []
    usersTotal.value = data.total || 0
  } catch (e) {
    ElMessage.error(e.message || '加载用户列表失败')
  } finally {
    loadingUsers.value = false
  }
}

async function loadMessages() {
  if (!currentUser.value) return
  loadingMessages.value = true
  try {
    const startAt = messageDateRange.value?.[0] || ''
    const endAt = messageDateRange.value?.[1] || ''
    const data = await adminApi.sessionUserMessages(currentUser.value.user_id, {
      keyword: messageKeyword.value,
      role_id: messageRoleId.value || '',
      start_at: startAt,
      end_at: endAt,
      page: messagesQuery.value.page,
      page_size: messagesQuery.value.page_size
    })
    messageList.value = data.list || []
    messagesTotal.value = data.total || 0
  } catch (e) {
    ElMessage.error(e.message || '加载聊天记录失败')
  } finally {
    loadingMessages.value = false
  }
}

function onSearchUsers() {
  usersQuery.value.page = 1
  loadUsers()
}

function onResetUsers() {
  keyword.value = ''
  userRoleId.value = undefined
  userDateRange.value = []
  usersQuery.value.page = 1
  loadUsers()
}

function onUsersPageChange(page) {
  usersQuery.value.page = page
  loadUsers()
}

function openUserMessages(row) {
  currentUser.value = row
  drawerVisible.value = true
  messagesQuery.value.page = 1
  messageKeyword.value = ''
  messageRoleId.value = undefined
  messageDateRange.value = []
  loadMessages()
}

function onSearchMessages() {
  messagesQuery.value.page = 1
  loadMessages()
}

function onResetMessages() {
  messageKeyword.value = ''
  messageRoleId.value = undefined
  messageDateRange.value = []
  messagesQuery.value.page = 1
  loadMessages()
}

function onMessagesPageChange(page) {
  messagesQuery.value.page = page
  loadMessages()
}

async function loadRoles() {
  try {
    const data = await adminApi.roles({ page: 1, page_size: 200 })
    roleOptions.value = data.list || []
  } catch (e) {
    ElMessage.error(e.message || '加载角色失败')
  }
}

function escapeHtml(input) {
  const str = String(input ?? '')
  return str
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function escapeRegExp(input) {
  return String(input ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlightText(text, keywordText) {
  const safe = escapeHtml(text)
  const kw = String(keywordText || '').trim()
  if (!kw) return safe
  const reg = new RegExp(`(${escapeRegExp(kw)})`, 'gi')
  return safe.replace(reg, '<mark style="background:#fff2a8;padding:0 2px;border-radius:2px;">$1</mark>')
}

async function onExportMessages() {
  if (!currentUser.value) return
  try {
    const startAt = messageDateRange.value?.[0] || ''
    const endAt = messageDateRange.value?.[1] || ''
    const res = await adminApi.exportSessionUserMessages(currentUser.value.user_id, {
      keyword: messageKeyword.value,
      role_id: messageRoleId.value || '',
      start_at: startAt,
      end_at: endAt
    })
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `user_${currentUser.value.user_id}_chat_messages.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
    ElMessage.success('导出成功')
  } catch (e) {
    ElMessage.error(e.message || '导出失败')
  }
}

onMounted(async () => {
  await loadRoles()
  await loadUsers()
})
</script>
